import type { QueryKey } from '@tanstack/react-query';

import { RevisionLicenseConclusionScope } from '../../../../../../../../shared/model-typings/revisionlicenseconclusion';
import { getRevisionConclusionsQueryKey } from '../../../../../../../hooks/api/correctionsV2/useGetRevisionConclusions';
import { getProjectDependencyQueryKey } from '../../../../../../../hooks/api/dependencies/useGetProjectDependency';
import {
  getReleaseGroupDependencyQueryKey,
} from '../../../../../../../hooks/api/dependencies/useGetReleaseGroupDependency';
import { getSnippetsBaseQueryKey } from '../../../../../../../hooks/api/snippets/shared';
import queryClient from '../../../../../../../ReactQuery/client';
import useToastedMutation from '../../../../../../../ReactQuery/useToastedMutation';
import { put } from '../../../../../../../redux/api';
import type { ConclusionRequestScope } from '../../../../../../../../shared/types/licenses';

export type ChooseParams = {
  /**
   * The dependency revision locator.
   */
  dependencyRevisionLocator: string;
  /**
   * The scope the dependency is being called from.
   */
  scope: ConclusionRequestScope;
  /**
   * The id of the resource to initiate a scan for. This is either a revision locator or a release ID.
   */
  originId?: string;
};

const unconcludeLicense = async ({
  chosenLicense,
  chooseParams,
}: {
  chosenLicense: string;
  chooseParams: ChooseParams;
}): Promise<void> => {
  await put('/api/license-conclusions/unconclude', {
    ...chooseParams,
    licenseId: chosenLicense,
  });
};

/**
 * Hook for upserting a license conclusion and showing a toast notification.
 */
export default function useUnconcludeLicense(chooseParams: ChooseParams) {
  return useToastedMutation({
    mutationFn: unconcludeLicense,
    successToastMessage: 'License unconcluded successfully.',
    errorToastMessage: 'Failed to unconclude license.',
    onSuccess: () => {
      const queryKeys: QueryKey[] = [];
      if (chooseParams.scope.scope === RevisionLicenseConclusionScope.PROJECT && chooseParams.originId) {
        const projectDepQueryKey = getProjectDependencyQueryKey(
          chooseParams.originId, chooseParams.dependencyRevisionLocator);
        const snippetQueryKey = getSnippetsBaseQueryKey(chooseParams.originId);

        queryKeys.push(projectDepQueryKey);
        queryKeys.push(snippetQueryKey);
      } else if (chooseParams.scope.scope === RevisionLicenseConclusionScope.RELEASE_GROUP && chooseParams.originId) {
        const releaseDepQueryKey = getReleaseGroupDependencyQueryKey(
          chooseParams.scope.releaseGroupId,
          Number(chooseParams.originId),
          chooseParams.dependencyRevisionLocator,
        );
        queryKeys.push(releaseDepQueryKey);
      } else if (chooseParams.scope.scope === RevisionLicenseConclusionScope.REVISION) {
        const revisionConclusionsQueryKey = getRevisionConclusionsQueryKey(
          chooseParams.dependencyRevisionLocator,
        );
        queryKeys.push(revisionConclusionsQueryKey);
      }
      queryKeys.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
  });
}
